import { TFunction } from 'i18next';
import { PLACE_ID_PATTERN } from '@bandido/shared/types';

import { countryName } from './destinations';
import { transferPointNameParam } from './transfers';
import { transferLuggageFromParams, transferLuggageSelection, transferLuggageSummary } from './transferLuggage';
import {
  SearchPassengerDraft,
  ferryPassengersFromParams,
  searchPassengersFromParam,
  transferPassengersFromParams,
} from './searchPassengers';

/**
 * Cómo se lee una búsqueda guardada, por vertical.
 *
 * Se resuelve al RENDERIZAR y no al guardar: así la etiqueta sale en el idioma
 * actual y no en el que estaba puesto cuando se buscó. Cada vertical sabe qué
 * significan SUS parámetros — un origen de tren no es un punto de traslado — y
 * por eso son funciones separadas y no una sola genérica.
 *
 * Son puras y no tocan el DOM: se prueban sin React.
 */

type Params = Record<string, string>;
type T = TFunction;

/** "2026-09-27" → "27 sep". El año se omite: casi siempre es el corriente. */
export function shortDate(value: string | undefined, locale: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
}

/** Une lo que exista con separadores, sin dejar " · · " por el camino. */
function join(parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(' · ');
}

function route(from?: string, to?: string): string {
  return [from, to].filter(Boolean).join(' → ');
}

function passengersLabel(
  passengers: readonly SearchPassengerDraft[] | null,
  t: T,
): string | undefined {
  if (!passengers?.length) return undefined;
  return (['adult', 'child', 'youth', 'senior'] as const)
    .map((type) => {
      const entries = passengers.filter((passenger) => passenger.type === type);
      if (!entries.length) return undefined;
      const ages = entries.map((passenger) => passenger.age.trim()).filter(Boolean);
      return `${t(`travelServices.passengerPicker.types.${type}`, {
        count: entries.length,
      })}${ages.length ? ` (${ages.join(', ')})` : ''}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function circuitCityLabels(value?: string): string {
  return (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const separator = entry.indexOf(':');
      return separator > 0 ? safeDecode(entry.slice(separator + 1)) : entry;
    })
    .join(', ');
}

function circuitDurationLabel(params: Params, t: T): string | undefined {
  const min = Number(params.dmin);
  const max = Number(params.dmax);
  if (min && max) return `${t('travelServices.circuits.duration')}: ${min}-${max}`;
  if (min) return `${t('travelServices.circuits.duration')}: ${min}+`;
  if (max) return `${t('travelServices.circuits.duration')}: ≤${max}`;
  return undefined;
}

export function describeRail(params: Params, t: T, locale: string): string {
  return join([
    // `origen`/`destino` llevan el `plc_*` (ver
    // RailSearchForm) y `origenNombre`/`destinoNombre` el nombre a mostrar.
    // Sin el setting, `origen` YA es el nombre (no hay *Nombre) — el
    // fallback cubre ambos formatos sin distinguirlos.
    route(params.origenNombre ?? params.origen, params.destinoNombre ?? params.destino),
    shortDate(params.fecha, locale),
    passengersLabel(searchPassengersFromParam(params.pax), t),
  ]);
}

export function describeBuses(params: Params, t: T, locale: string): string {
  return join([
    route(params.origenNombre ?? params.origen, params.destinoNombre ?? params.destino),
    shortDate(params.fecha, locale),
    params.fechaVuelta && t('travelServices.transfers.roundTrip'),
    params.pax && t('travelServices.paxShort', { count: Number(params.pax) }),
  ]);
}

export function describeFerry(params: Params, t: T, locale: string): string {
  return join([
    route(params.origenNombre ?? params.origen, params.destinoNombre ?? params.destino),
    shortDate(params.fecha, locale),
    params.fechaVuelta && t('travelServices.transfers.roundTrip'),
    passengersLabel(ferryPassengersFromParams(params), t),
  ]);
}

export function describeTransfers(params: Params, t: T, locale: string): string {
  // `oiata`/`diata` puede ser un IATA de 3 letras o un `plc_*` (#2178, mismo
  // param en los dos submodos) — con lugar, el nombre amigable para mostrar
  // viaja aparte (`transferPointNameParam`), nunca en el id crudo. Se lee
  // por la MISMA función que lo escribe (`TransfersSearchForm.tsx`), no un
  // literal a mano — un casing distinto ahí ya se coló una vez en silencio.
  return join([
    route(
      params[transferPointNameParam('o')] ?? params.oiata ?? params.onombre,
      params[transferPointNameParam('d')] ?? params.diata ?? params.dnombre,
    ),
    shortDate(params.fecha, locale),
    params.viaje === 'ROUNDTRIP' && t('travelServices.transfers.roundTrip'),
    passengersLabel(transferPassengersFromParams(params), t),
    transferLuggageSummary(transferLuggageSelection(transferLuggageFromParams(params)), t),
  ]);
}

export function describeCars(params: Params, _t: T, locale: string): string {
  const desde = shortDate(params.pickUpDate, locale);
  const hasta = shortDate(params.dropOffDate, locale);
  return join([
    params.pickUpNombre ?? params.pickUpCode ?? params.pais,
    desde && hasta ? `${desde} – ${hasta}` : desde,
  ]);
}

export function describeTours(params: Params, t: T, locale: string): string {
  const from = shortDate(params.desde, locale);
  const to = shortDate(params.hasta, locale);
  return join([
    params.destinoNombre ?? params.destino,
    from && to ? `${from} – ${to}` : from,
    params.pax && t('travelServices.paxShort', { count: Number(params.pax) }),
  ]);
}

export function describeCircuits(params: Params, t: T, locale: string): string {
  return join([
    params.circuitoNombre ?? params.circuito ?? circuitCityLabels(params.ciudades),
    !params.circuito && circuitDurationLabel(params, t),
    shortDate(params.desde, locale),
  ]);
}

export function describeEsims(params: Params, t: T, _locale: string): string {
  const destination =
    params.tipo === 'local' && PLACE_ID_PATTERN.test(params.destino ?? '')
      ? params.destinoNombre ?? params.destino
      : params.tipo === 'global' && params.region
        ? params.destinoNombre ?? params.region
        : undefined;
  if (!destination) return '';
  return join([
    destination,
    params.tipo && t(`travelServices.esims.${params.tipo}`),
  ]);
}

export function describePasses(params: Params, t: T, locale: string): string {
  const pax = (params.pax ?? '').split(',').filter(Boolean).length || 1;
  return join([
    countryName(params.residencia ?? '', locale) || params.residencia,
    t('travelServices.paxShort', { count: pax }),
  ]);
}
